import { ReportSdk } from "lib-klab-sdks";

type ISODateTime = string;

type DateRange = { startDate: string; endDate?: string };

const DEFAULT_SESSIONS_PER_DAY = 2;

export const webActivityReportsSdkMock = {
    getWebActivitiesList: (
        employeeId = 123,
        dateRange: DateRange = buildDefaultLast30DaysRange(),
        page = 0,
        size = 20
    ): ReportSdk.WebActivityListResponse => {
        const normalizedRange = normalizeRange(dateRange);

        const all = generateWebActivityItemsForRange(normalizedRange, DEFAULT_SESSIONS_PER_DAY);

        const totalElements = all.length;
        const safePage = Math.max(0, Number(page) || 0);
        const safeSize = Math.max(1, Number(size) || 20);
        const totalPages = Math.max(1, Math.ceil(totalElements / safeSize));

        const start = safePage * safeSize;
        const end = start + safeSize;

        return {
            metadata: {
                employeeId,
                dateRange: normalizedRange,
            },
            content: all.slice(start, end),
            page: {
                page: safePage,
                size: safeSize,
                totalElements,
                totalPages,
            },
        } as ReportSdk.WebActivityListResponse;
    },

    getWebsTopWithPercentage: (
        employeeId = 123,
        dateRange: DateRange = buildDefaultLast30DaysRange()
    ): ReportSdk.GroupedWebsResponse => {
        const normalizedRange = normalizeRange(dateRange);

        const all = generateWebActivityItemsForRange(normalizedRange, DEFAULT_SESSIONS_PER_DAY);

        const totals = new Map<string, number>();
        for (const it of all) {
            const d = String(it.domain || "").toLowerCase();
            totals.set(d, (totals.get(d) ?? 0) + (it.totalSeconds ?? 0));
        }

        const sorted = Array.from(totals.entries())
            .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
            .slice(0, 8);

        const content: ReportSdk.GroupedWebItem[] = sorted.map(([domain, totalSeconds]) => ({
            domain,
            totalSeconds,
            classification: classifyDomain(domain),
        }));

        const totalSecondsAll = content.reduce((acc, x) => acc + (x.totalSeconds ?? 0), 0);

        return {
            metadata: {
                employeeId,
                dateRange: normalizedRange,
                totalSeconds: totalSecondsAll,
            },
            content,
        } as ReportSdk.GroupedWebsResponse;
    },
};

function normalizeRange(range: DateRange): Required<DateRange> {
    const start = parseYmd(range.startDate);
    const end = parseYmd(range.endDate ?? range.startDate);

    const s = start.getTime();
    const e = end.getTime();

    if (s <= e) return { startDate: toYmd(start), endDate: toYmd(end) };
    return { startDate: toYmd(end), endDate: toYmd(start) };
}

function generateWebActivityItemsForRange(
    dateRange: Required<DateRange>,
    sessionsPerDay = 2
): ReportSdk.WebActivityItem[] {
    const start = parseYmd(dateRange.startDate);
    const end = parseYmd(dateRange.endDate);

    const days = enumerateDaysInclusive(start, end);

    const webs = [
        {
            browser: "Chrome",
            title: "Klab Admin | Dashboard",
            domain: "klab.app",
            url: "https://klab.app/admin/dashboard",
            active: 60 * 60 * 1 + 60 * 10,
            idle: 60 * 8,
        },
        {
            browser: "Chrome",
            title: "Jira | MOPROD-286 - Reports SDK",
            domain: "jira.atlassian.com",
            url: "https://jira.atlassian.com/browse/MOPROD-286",
            active: 60 * 50,
            idle: 60 * 6,
        },
        {
            browser: "Edge",
            title: "Docs | OpenAPI contract - Report API",
            domain: "docs.google.com",
            url: "https://docs.google.com/document/d/xxxx",
            active: 60 * 40,
            idle: 60 * 7,
        },
        {
            browser: "Chrome",
            title: "GitHub | klab-micros-ui PRs",
            domain: "github.com",
            url: "https://github.com/org/repo/pulls",
            active: 60 * 35,
            idle: 60 * 10,
        },
        {
            browser: "Chrome",
            title: "YouTube | Música para programar",
            domain: "youtube.com",
            url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
            active: 60 * 8,
            idle: 60 * 20,
        },
        {
            browser: "Chrome",
            title: "Notion | Plan semanal",
            domain: "notion.so",
            url: "https://www.notion.so/xxxxx",
            active: 60 * 25,
            idle: 60 * 5,
        },
        {
            browser: "Edge",
            title: "Stack Overflow | Angular InjectionToken No provider",
            domain: "stackoverflow.com",
            url: "https://stackoverflow.com/questions/xxxx",
            active: 60 * 18,
            idle: 60 * 4,
        },
        {
            browser: "Chrome",
            title: "Figma | Design System - Core UI",
            domain: "figma.com",
            url: "https://www.figma.com/file/xxxxx",
            active: 60 * 30,
            idle: 60 * 6,
        },
    ];

    const result: ReportSdk.WebActivityItem[] = [];

    for (let dIndex = 0; dIndex < days.length; dIndex++) {
        const day = days[dIndex];

        for (let s = 0; s < sessionsPerDay; s++) {
            const web = webs[(dIndex * sessionsPerDay + s) % webs.length];

            const startHour = [9, 11, 15, 18][s] ?? 9;
            const minuteJitter = (dIndex * 7 + s * 11) % 50;
            const startAt = withTime(day, startHour, minuteJitter, 0);

            const activeSeconds = jitter(web.active, dIndex, s);
            const idleSeconds = jitter(web.idle, dIndex + 3, s + 2);
            const totalSeconds = activeSeconds + idleSeconds;
            const endedAt = addSeconds(startAt, totalSeconds);

            result.push({
                browser: web.browser,
                title: web.title,
                domain: web.domain,
                url: web.url,
                startAt: toIsoZ(startAt) as ISODateTime,
                endedAt: toIsoZ(endedAt) as ISODateTime,
                activeSeconds,
                idleSeconds,
                totalSeconds,
            } as any);
        }
    }

    result.sort((a, b) => String(b.startAt).localeCompare(String(a.startAt)));

    return result;
}

function classifyDomain(domain: string): ReportSdk.GroupedWebItem.ClassificationEnum {
    const d = String(domain || "").toLowerCase();

    if (
        d.includes("klab") ||
        d.includes("jira") ||
        d.includes("github") ||
        d.includes("stackoverflow") ||
        d.includes("figma") ||
        d.includes("docs.google") ||
        d.includes("google.com")
    ) {
        return ReportSdk.GroupedWebItem.ClassificationEnum.Productive;
    }

    if (d.includes("youtube") || d.includes("tiktok") || d.includes("facebook") || d.includes("instagram")) {
        return ReportSdk.GroupedWebItem.ClassificationEnum.Unproductive;
    }

    return ReportSdk.GroupedWebItem.ClassificationEnum.Neutral;
}

function buildDefaultLast30DaysRange(): Required<DateRange> {
    const end = startOfToday();
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    return { startDate: toYmd(start), endDate: toYmd(end) };
}

function startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseYmd(ymd: string): Date {
    const [y, m, d] = String(ymd).split("-").map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

function enumerateDaysInclusive(start: Date, end: Date): Date[] {
    const res: Date[] = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);

    const last = new Date(end);
    last.setHours(0, 0, 0, 0);

    while (cur.getTime() <= last.getTime()) {
        res.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }

    return res;
}

function withTime(day: Date, hh: number, mm: number, ss: number): Date {
    const d = new Date(day);
    d.setHours(hh, mm, ss, 0);
    return d;
}

function addSeconds(date: Date, seconds: number): Date {
    return new Date(date.getTime() + seconds * 1000);
}

function toIsoZ(d: Date): string {
    return new Date(d.getTime()).toISOString();
}

function jitter(baseSeconds: number, a: number, b: number): number {
    const delta = ((a * 17 + b * 31) % 9) * 60;
    return Math.max(60, Math.floor(baseSeconds + delta));
}

function toYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}